import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';

import { FormConfigurationModel } from '../../../../models/forms/form-configuration.model';
import { FormVersionConfigurationModel } from '../../../../models/forms/form-version-configuration.model';
import { FormItemConfigurationModel } from '../../../../models/forms/form-item-configuration.model';
import { FormParagraphConfigurationModel } from '../../../../models/forms/form-paragraph-configuration.model';
import { FormItemDataModel } from '../../../../models/forms/data/form-item-data.model';
import { FormParagraphDataModel } from '../../../../models/forms/data/form-paragraph-data.model';
import { FormResultDataModel } from '../../../../models/forms/data/form-result-data.model';

export interface FormsBuilderState {
  selectedForm: FormConfigurationModel | null;
  selectedVersion: FormVersionConfigurationModel | null;
}

const STORAGE_KEY_FORM = 'forms-builder-selected-form';
const STORAGE_KEY_VERSION = 'forms-builder-selected-version';

@Injectable({
  providedIn: 'root'
})
export class FormsBuilderStateService {
  private formSubject = new BehaviorSubject<FormConfigurationModel | null>(null);
  private versionSubject = new BehaviorSubject<FormVersionConfigurationModel | null>(null);

  selectedForm$: Observable<FormConfigurationModel | null> = this.formSubject.asObservable();
  selectedVersion$: Observable<FormVersionConfigurationModel | null> = this.versionSubject.asObservable();

  // DFormIds confirmed to exist on the server this session. Lets the finder trust a cached form
  // straight away when it is re-created (e.g. on returning from the editor) instead of reporting a
  // saved form as unsaved until its background re-fetch lands. In memory only: after a reload the
  // finder has to check with the server again.
  private confirmedSavedFormIds = new Set<string>();

  // Version created by the finder's document import that still needs one Save in the editor: the
  // import persists the version tree without master items, which only the editor's save creates.
  // One-shot — consumed by the editor when it opens that version.
  private pendingImportSaveVersionId: string | null = null;

  constructor(private router: Router) {
    this.loadFromLocalStorage();
  }

  get selectedForm(): FormConfigurationModel | null {
    return this.formSubject.getValue();
  }

  get selectedVersion(): FormVersionConfigurationModel | null {
    return this.versionSubject.getValue();
  }

  setSelectedForm(form: FormConfigurationModel | null): void {
    this.formSubject.next(form);
    if (form) {
      this.persistToLocalStorage(STORAGE_KEY_FORM, form);
    } else {
      try {              
        localStorage.removeItem(STORAGE_KEY_FORM);
      } catch (e: unknown) {
        console.error(`Error removing ${STORAGE_KEY_FORM} from localStorage:`, e);
      }
    }
  }

  setSelectedVersion(version: FormVersionConfigurationModel | null): void {
    this.versionSubject.next(version);
    if (version) {
      this.persistToLocalStorage(STORAGE_KEY_VERSION, version);
    } else {
      try {              
        localStorage.removeItem(STORAGE_KEY_VERSION);
      } catch (e: unknown) {
        console.error(`Error removing ${STORAGE_KEY_VERSION} from localStorage:`, e);
      }
    }
  }

  setFormConfirmedSaved(formId: string, saved: boolean): void {
    if (!formId) return;
    if (saved) {
      this.confirmedSavedFormIds.add(formId);
    } else {
      this.confirmedSavedFormIds.delete(formId);
    }
  }

  isFormConfirmedSaved(formId: string): boolean {
    return !!formId && this.confirmedSavedFormIds.has(formId);
  }

  setPendingImportSave(versionId: string): void {
    this.pendingImportSaveVersionId = versionId || null;
  }

  /** True (once) when `versionId` is the imported version still awaiting its first editor save. */
  consumePendingImportSave(versionId: string): boolean {
    if (!versionId || this.pendingImportSaveVersionId !== versionId) return false;
    this.pendingImportSaveVersionId = null;
    return true;
  }

  // Best-effort only — used to survive a page refresh. Large forms with many
  // versions can exceed the per-origin localStorage quota; a failure here must
  // not block navigation, since in-memory state (formSubject/versionSubject)
  // already has what the UI needs.
  private persistToLocalStorage(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Error persisting ${key} to localStorage:`, e);
    }
  }

  clearState(): void {
    this.formSubject.next(null);
    this.versionSubject.next(null);
    // Best-effort — mirrors persistToLocalStorage: a storage failure (e.g. Safari private mode)
    // must not throw, since the in-memory subjects above already hold the cleared state.
    try {
      localStorage.removeItem(STORAGE_KEY_FORM);
      localStorage.removeItem(STORAGE_KEY_VERSION);
    } catch (e) {
      console.error('Error clearing forms builder state from localStorage:', e);
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const formJson = localStorage.getItem(STORAGE_KEY_FORM);
      if (formJson) {
        const form = JSON.parse(formJson) as FormConfigurationModel;
        this.formSubject.next(form);
      }

      const versionJson = localStorage.getItem(STORAGE_KEY_VERSION);
      if (versionJson) {
        const version = JSON.parse(versionJson) as FormVersionConfigurationModel;
        this.versionSubject.next(version);
      }
    } catch (e) {
      console.error('Error loading forms builder state from localStorage:', e);
      this.clearState();
    }
  }

  navigateToEditor(formId: string, versionId: string, mode: 'edit' | 'view' = 'view'): void {
    this.router.navigate(['/forms/builder/editor', formId, versionId, mode]);
  }

  navigateToFinder(): void {
    this.router.navigate(['/forms/builder/finder']);
  }

  updateVersionInForm(updatedVersion: FormVersionConfigurationModel): void {
    const currentForm = this.selectedForm;
    if (currentForm && currentForm.cfgDFormVersions) {
      const index = currentForm.cfgDFormVersions.findIndex(
        v => v.DFormVersionId === updatedVersion.DFormVersionId
      );
      if (index >= 0) {
        currentForm.cfgDFormVersions[index] = updatedVersion;
        this.setSelectedForm(currentForm);
      }
    }
  }

  // Spoof data conversion utilities for live preview builder
  createSpoofItemData(cfg: FormItemConfigurationModel, paragraphId?: string, validValues?: any[]): FormItemDataModel {
    const dataItem = new FormItemDataModel();
    dataItem.dataFormItemId = cfg.DFormItemId || '';
    dataItem.dataFormParagraphID = paragraphId || cfg.DFormParagraphId || '';
    dataItem.ItemName = cfg.DFormItemName;
    dataItem.DFormItemId = cfg.DFormItemId;
    dataItem.HasChildren = cfg.HasChildren;
    dataItem.ParentdataFormItemId = cfg.ParentDFormItemId;
    dataItem.cfgDFormItem = JSON.parse(JSON.stringify(cfg));
    dataItem.dataFormResults = [];

    // Hydrate ValidValue if only ValidValueId is set (e.g. newly-created items from prebuilt templates)
    if (dataItem.cfgDFormItem.ValidValueId && !dataItem.cfgDFormItem.ValidValue && validValues?.length) {
      const match = validValues.find((v: any) => v.ValidValueId === dataItem.cfgDFormItem.ValidValueId);
      if (match) {
        dataItem.cfgDFormItem.ValidValue = JSON.parse(JSON.stringify(match));
      }
    }

    // Initialize RESULT with empty FormResultDataModel
    const result = new FormResultDataModel();
    result.Result = cfg.DefaultValue || '';
    result.dataFormItemId = cfg.DFormItemId || '';
    dataItem.RESULT = result;
    dataItem.RESULTS = [];  // Must be empty array, not null — checkbox/radio list controls call .find()/.forEach() on RESULTS

    return dataItem;
  }

  createSpoofParagraphData(cfg: FormParagraphConfigurationModel): FormParagraphDataModel {
    const dataParagraph = new FormParagraphDataModel();
    dataParagraph.dataFormParagraphID = cfg.DFormParagraphId || '';
    dataParagraph.DFormParagraphId = cfg.DFormParagraphId;
    dataParagraph.ParagraphOrder = cfg.ParagraphOrder;
    dataParagraph.cfgDFormParagraph = JSON.parse(JSON.stringify(cfg));
    dataParagraph.dataFormItems = [];

    if (cfg.cfgDFormItems) {
      dataParagraph.dataFormItems = cfg.cfgDFormItems
        .filter(item => !item.xauditDeleted)
        .map(item => this.createSpoofItemData(item, cfg.DFormParagraphId));
    }

    return dataParagraph;
  }
}
